// mmu-loader.js — slimmed MMU type registry (demo).
//
// KFE Web's `data/mmu-loader.js` is a sql.js / JSON-backed loader (master MMU
// dictionary + per-item ordered lists + L2BType index). The demo doesn't need
// any of that plumbing — `demo-content-provider.js` already serves resolved,
// ordered MMUs straight out of `content/demo-mmus.json` (Brief 002). What the
// COPIED-VERBATIM engines still need from this module is the `Mmu` *type* (they
// reference it via `import("../data/mmu-loader.js").Mmu`), so we keep the typedef
// here unchanged. We also keep `l2bModeRenderFlags` — the per-mode stimulus
// rendering rule the screens consume — and `stem`, the filename→stem helper.
//
// Everything sql.js / fetch / item-list / mode-content-predicate is dropped;
// Brief 004 (language onboarding) reintroduces any mode plumbing it needs.

export { stem } from "./media-paths.js";

/**
 * The MMU shape the engines and screens hold. Kept field-for-field identical to
 * KFE Web's `Mmu` so the copied engines type-check against it unchanged.
 *
 * @typedef {object} Mmu
 * @property {number} mmuId
 * @property {string} phrase
 * @property {string} phraseZh
 * @property {string} imageFileName
 * @property {string} audioFileName
 * @property {boolean} isHaai
 * @property {boolean} isL2b
 * @property {boolean} isSelfRecord
 * @property {boolean} isLargeImage
 * @property {number}  masteryTarget
 * @property {string} wordKey
 * @property {string} audioStem
 * @property {string} imageStem
 */

/**
 * Per-mode rendering flags for the L2B exercise screens (Brief 101), mirroring
 * iOS KFELanguageBodyViewController.m's per-viewType branch:
 *   • "Audio only"     → stimulus is sound; the phrase text is HIDDEN and audio
 *     auto-plays on present.
 *   • "Text only"      → stimulus is text; the phrase shows and audio does NOT
 *     auto-play (still available on the replay button).
 *   • "Audio and Text" → both: phrase shows AND audio auto-plays.
 * A null / unrecognised mode keeps the default (show text + auto-play). The demo
 * ships English-target audio-only content but carries no per-item L2BType yet,
 * so screens call this with `null` and get the show-text + auto-play default
 * (Brief 004 wires real modes).
 * @param {string | null | undefined} l2bType
 * @returns {{ showText: boolean, autoPlay: boolean }}
 */
export function l2bModeRenderFlags(l2bType) {
  switch (l2bType) {
    case "Audio only":     return { showText: false, autoPlay: true };
    case "Text only":      return { showText: true,  autoPlay: false };
    case "Audio and Text": return { showText: true,  autoPlay: true };
    default:               return { showText: true,  autoPlay: true };
  }
}
